import { ThemeEvents } from '@theme/events';

class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (priceContainer) {
      setTimeout(() => this.updateDiscount(priceContainer), 0);
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  updatePrice = (event) => {
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!currentPrice) return;

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');

    if (!newPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
      const updatedContainer = this.querySelector('[ref="priceContainer"]');
      if (updatedContainer) {
        this.updateDiscount(updatedContainer, event.detail?.resource);
      }
    }
  };

  /**
   * Parses a price string from the DOM (e.g. "434,99 $", "1 879 $", "2,304.99", "$1,879") into a number.
   * - French: comma as decimal (always exactly 2 fractional digits, e.g. "434,99", "1 234,99").
   * - English: comma as thousands separator (e.g. "1,879", "2,304.99"); dot is decimal.
   *
   * A comma is only treated as decimal when there is no dot and 1–2 digits follow the last comma
   * (cents), so English thousands like "1,879" still strip commas → 1879.
   */
  parsePriceFromDisplay(priceStr) {
    if (!priceStr || typeof priceStr !== 'string') return NaN;
    const raw = priceStr.trim().match(/[\d\s,.]+/);
    if (!raw) return NaN;
    let s = raw[0].replace(/\s/g, '');
    const lastComma = s.lastIndexOf(',');
    const hasDot = s.includes('.');
    if (lastComma !== -1) {
      const afterComma = s.slice(lastComma + 1);
      const commaIsDecimalSeparator = !hasDot && /^\d{1,2}$/.test(afterComma);
      if (commaIsDecimalSeparator) {
        s = s.replace(',', '.');
      } else {
        s = s.replace(/,/g, '');
      }
    }
    return parseFloat(s) || NaN;
  }

  updateDiscount(container, variantData = null) {
    if (!container) return;

    if (!variantData) {
      const picker = container.closest('.shopify-section, dialog')?.querySelector('variant-picker, nova-variant-picker');
      const script = picker?.querySelector('script[type="application/json"]');
      if (script) {
        try {
          variantData = JSON.parse(script.textContent);
        } catch (e) {
          return;
        }
      }
    }

    const saleEl = container.querySelector('.nova-price--sale, .nova-price-sale') ||
                   (container.querySelector('.nova-compare-at-price, .nova-price-original') ?
                    container.querySelector('.nova-price') : null);
    const compareEl = container.querySelector('.nova-compare-at-price, .nova-price-original');
    const badge = container.querySelector('.nova-discount-badge, .nova-discount-tag');

    if (!compareEl || !badge || !saleEl) {
      if (badge) badge.style.display = 'none';
      return;
    }

    let salePrice, comparePrice;

    if (variantData?.price && variantData?.compare_at_price) {
      salePrice = variantData.price / 100;
      comparePrice = variantData.compare_at_price / 100;
    } else {
      const saleText = saleEl.textContent.trim();
      const compareText = compareEl.textContent.trim();
      salePrice = this.parsePriceFromDisplay(saleText);
      comparePrice = this.parsePriceFromDisplay(compareText);
      if (Number.isNaN(salePrice) || Number.isNaN(comparePrice)) return;
    }

    if (!salePrice || !comparePrice || comparePrice <= salePrice) {
      badge.style.display = 'none';
      return;
    }

    const discount = Math.round(((comparePrice - salePrice) / comparePrice) * 100);
    const discount_amount = comparePrice - salePrice;
    
    // Get current locale for formatting
    const locale = window.Shopify?.locale || 'fr';
    const isFrench = locale.toLowerCase().startsWith('fr');

    if (discount > 0 && discount < 10) {
      // Format discount amount based on language
      const isRound = (discount_amount * 100) % 100 === 0;
      let discountFormatted;
      
      if (isFrench) {
        if (isRound) {
          discountFormatted = Math.round(discount_amount).toLocaleString('fr-CA');
        } else {
          discountFormatted = discount_amount.toLocaleString('fr-CA', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        badge.textContent = `-${discountFormatted} $`;
      } else {
        if (isRound) {
          discountFormatted = Math.round(discount_amount).toLocaleString('en-US');
        } else {
          discountFormatted = discount_amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }
        badge.textContent = `-$${discountFormatted}`;
      }
      badge.style.display = '';
    } else if (discount >= 10) {
      badge.textContent = `-${discount}%`;
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
